import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartAddPendingEvent, CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { refreshFreeShippingVisibility } from '@theme/free-shipping-visibility';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/** Distance from bottom of quick-add scroll area (px) before showing "See more" PDP link */
const QUICK_ADD_SEE_MORE_THRESHOLD_PX = 100;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    // Card links keep the carton variant for PDP navigation; slideout uses
    // swatch or filtered size + matching carton selection when present.
    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card or server-rendered state.
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const { selectedVariantId: resolvedCartonId } = this.dataset;
    if (resolvedCartonId) return resolvedCartonId;

    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const swatchVariantId = productCard?.getSelectedVariantId();
    if (swatchVariantId) return swatchVariantId;

    const variantInput = this.querySelector('input[name="id"]:not([disabled])');
    if (variantInput instanceof HTMLInputElement && variantInput.value) {
      return variantInput.value;
    }

    return null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const dialogComponent = this.#getQuickAddDialog();
    if (dialogComponent) {
      this.#stayVisibleUntilDialogCloses(dialogComponent);
      dialogComponent.showDialog();
      dialogComponent.setLoadingState(true);
    }

    try {
      const currentUrl = this.productPageUrl;
      if (!currentUrl) return;

      // Check if we have cached content for this URL
      let productInformationContent = this.#cachedContent.get(currentUrl);

      if (!productInformationContent) {
        // Fetch and cache the product information section content
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const productInformationElement = html.querySelector('[data-testid="product-information"]');

          if (productInformationElement) {
            // Cache the cloned element to avoid modifying the original
            productInformationContent = /** @type {Element} */ (productInformationElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productInformationContent);
          }
        }
      }

      if (productInformationContent) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productInformationContent.cloneNode(true));
        await this.updateQuickAddModal(freshContent, currentUrl);
        this.#updateVariantPicker(productInformationContent);
        dialogComponent?.setQuickAddProductUrl(currentUrl);
      } else {
        dialogComponent?.setQuickAddProductUrl('');
      }
    } finally {
      dialogComponent?.setLoadingState(false);
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #getQuickAddDialog() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return null;
    return dialogComponent;
  }

  #openQuickAddModal = () => {
    const dialogComponent = this.#getQuickAddDialog();
    if (!dialogComponent) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = this.#getQuickAddDialog();
    if (!dialogComponent) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productInformationContent - Product information section content
   * @param {string} [fetchedUrl] - The product page URL used for the fetch
   */
  async updateQuickAddModal(productInformationContent, fetchedUrl) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productInformationContent || !modalContent) return;

    morph(modalContent, productInformationContent);

    this.#syncVariantSelection(modalContent, fetchedUrl);
    await refreshFreeShippingVisibility(modalContent);
    this.#notifyDynamicContent(modalContent);
  }

  /**
   * Re-initialize scripts for content injected into the quick-add modal.
   * @param {HTMLElement} scope
   */
  #notifyDynamicContent(scope) {
    document.dispatchEvent(
      new CustomEvent('theme:dynamic-content', {
        bubbles: true,
        detail: { scope },
      })
    );
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected.
   * Only applies to dual add/choose quick-add controls.
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!this.dataset.productOptionsCount) return;
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal when the
   * server-rendered fetch did not already pre-select the requested variant.
   * @param {Element} modalContent - The modal content element
   * @param {string} [fetchedUrl] - The product page URL used for the fetch
   */
  #syncVariantSelection(modalContent, fetchedUrl) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    if (fetchedUrl) {
      try {
        const url = new URL(fetchedUrl, window.location.origin);
        if (url.searchParams.get('variant') === selectedVariantId) {
          const checkedInput = modalContent.querySelector(
            `input[type="radio"][data-variant-id="${selectedVariantId}"]:checked`
          );
          if (checkedInput) return;
        }
      } catch {
        // Fall through to manual sync.
      }
    }

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  /** @type {HTMLElement | null} */
  #seeMoreScrollTarget = null;

  /** @type {ResizeObserver | null} */
  #seeMoreResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(CartAddPendingEvent.eventName, this.handleCartAddPending, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#hideSeeMoreOnClose);

    this.#bindSeeMoreScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#unbindSeeMoreScroll();
    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#hideSeeMoreOnClose);
  }

  #bindSeeMoreScroll() {
    const content = this.querySelector('#quick-add-modal-content');
    if (!(content instanceof HTMLElement)) return;

    this.#seeMoreScrollTarget = content;
    content.addEventListener('scroll', this.#onSeeMoreScrollTargetScroll, { passive: true });

    this.#seeMoreResizeObserver = new ResizeObserver(() => this.#updateSeeMoreVisibility());
    this.#seeMoreResizeObserver.observe(content);
  }

  #unbindSeeMoreScroll() {
    this.#seeMoreResizeObserver?.disconnect();
    this.#seeMoreResizeObserver = null;
    this.#seeMoreScrollTarget?.removeEventListener('scroll', this.#onSeeMoreScrollTargetScroll);
    this.#seeMoreScrollTarget = null;
  }

  #onSeeMoreScrollTargetScroll = () => {
    this.#updateSeeMoreVisibility();
  };

  #hideSeeMoreOnClose = () => {
    const link = /** @type {HTMLAnchorElement | null} */ (this.querySelector('#quick-add-see-more'));
    if (!(link instanceof HTMLAnchorElement)) return;

    link.classList.remove('quick-add-modal__see-more--visible');
    link.setAttribute('aria-hidden', 'true');
    link.tabIndex = -1;
  };

  /**
   * @param {string} productUrl
   */
  setQuickAddProductUrl(productUrl) {
    const link = /** @type {HTMLAnchorElement | null} */ (this.querySelector('#quick-add-see-more'));
    if (!(link instanceof HTMLAnchorElement)) return;

    if (!productUrl) {
      link.hidden = true;
      link.removeAttribute('href');
      link.classList.remove('quick-add-modal__see-more--visible');
      link.setAttribute('aria-hidden', 'true');
      link.tabIndex = -1;
      return;
    }

    link.hidden = false;
    link.href = productUrl;
    link.setAttribute('aria-hidden', 'true');
    link.classList.remove('quick-add-modal__see-more--visible');
    link.tabIndex = -1;

    requestAnimationFrame(() => this.#updateSeeMoreVisibility());
  }

  #updateSeeMoreVisibility() {
    const link = /** @type {HTMLAnchorElement | null} */ (this.querySelector('#quick-add-see-more'));
    const content = this.querySelector('#quick-add-modal-content');
    const { dialog } = this.refs;

    if (!(link instanceof HTMLAnchorElement) || !content || !dialog?.open || link.hidden) return;

    const href = link.getAttribute('href');
    if (!href || href === '#') {
      link.classList.remove('quick-add-modal__see-more--visible');
      link.setAttribute('aria-hidden', 'true');
      link.tabIndex = -1;
      return;
    }

    const { scrollTop, clientHeight, scrollHeight } = content;
    const nearBottom =
      scrollHeight <= clientHeight + QUICK_ADD_SEE_MORE_THRESHOLD_PX ||
      scrollTop + clientHeight >= scrollHeight - QUICK_ADD_SEE_MORE_THRESHOLD_PX;

    link.classList.toggle('quick-add-modal__see-more--visible', nearBottom);
    link.setAttribute('aria-hidden', nearBottom ? 'false' : 'true');
    link.tabIndex = nearBottom ? 0 : -1;
  }

  /**
   * Close the quick-add dialog immediately when add-to-cart starts
   * and the cart drawer opens.
   */
  handleCartAddPending = () => {
    this.closeDialog();
  };

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));
    const seeMoreLink = /** @type {HTMLAnchorElement | null} */ (this.querySelector('#quick-add-see-more'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    if (seeMoreLink) {
      seeMoreLink.href = anchorElement.href;
      this.#updateSeeMoreVisibility();
    }
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };

  /**
   * Apply loading state while new quick-add content is fetched.
   * @param {boolean} isLoading
   */
  setLoadingState(isLoading) {
    this.toggleAttribute('loading-data', isLoading);
    this.classList.toggle('is-loading', isLoading);

    const seeMore = /** @type {HTMLAnchorElement | null} */ (this.querySelector('#quick-add-see-more'));
    if (!(seeMore instanceof HTMLAnchorElement)) return;

    if (isLoading) {
      seeMore.classList.remove('quick-add-modal__see-more--visible');
      seeMore.setAttribute('aria-hidden', 'true');
      seeMore.tabIndex = -1;
    } else {
      this.#updateSeeMoreVisibility();
    }
  }

}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
